// frontend/src/components/layout/MainLayout.js
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../Header';
import HeroSection from '../HeroSection';
import RegulationsSidebar from '../RegulationsSidebar';
import MaterialsHubSidebar from '../MaterialsHubSidebar';
import Footer from '../Footer';
import { lawApiService } from '../../services/lawApi';
import { useVisitorTracking } from '../../hooks/useVisitorTracking';

/**
 * 공통 레이아웃 컴포넌트
 * @param {boolean} includeHero - 히어로 섹션 포함 여부
 * @param {React.ReactNode} children - 자식 컴포넌트
 * @param {boolean} isSearchBarVisible - 검색창 표시 여부
 * @param {boolean} hideSidebar - 사이드바 숨김 여부
 */
const MainLayout = ({ includeHero = false, children, isSearchBarVisible = true, hideSidebar = false }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isSafetyMaterialsSection =
    location.pathname === '/safety-materials' ||
    location.pathname === '/safety-materials/' ||
    location.pathname.startsWith('/safety-materials/') ||
    location.pathname === '/safety-equipment' ||
    location.pathname.startsWith('/safety-equipment/');
  const isBoardPage = location.pathname.startsWith('/board') || location.pathname.startsWith('/field-qna');
  const shouldHideSidebar = hideSidebar || isBoardPage;
  const [laws, setLaws] = useState([]);
  const [loadingLaws, setLoadingLaws] = useState(false);

  // 법령 목록 로드
  useEffect(() => {
    if (!isHomePage && !shouldHideSidebar) {
      const fetchLaws = async () => {
        setLoadingLaws(true);
        try {
          const lawsList = await lawApiService.getLawList();
          setLaws(lawsList);
        } catch (error) {
          console.error('법령 목록 로드 오류:', error);
        } finally {
          setLoadingLaws(false);
        }
      };
      fetchLaws();
    }
  }, [isHomePage, shouldHideSidebar]);

  useVisitorTracking(location.pathname, location.search);

  return (
    <div className="min-h-screen flex flex-col">
      {/* 헤더 (네비게이션 포함) */}
      <Header isSearchBarVisible={isSearchBarVisible} />
      
      {/* 히어로 섹션 (홈페이지에만 표시) */}
      {includeHero && <HeroSection />}
      
      {/* 메인 콘텐츠 영역 - 사이드바와 콘텐츠 구역을 플렉스로 배치 */}
      <div className={`flex flex-1 ${isHomePage ? 'bg-white md:bg-gray-50' : 'bg-gray-50'}`}>
        {/* 사이드바: 안전자재 허브 / 보호구 가이드 / 법규 */}
        {!isHomePage && !shouldHideSidebar && (
          <div className="flex-shrink-0">
            {isSafetyMaterialsSection ? (
              <div className="hidden lg:block">
                <MaterialsHubSidebar isSearchBarVisible={isSearchBarVisible} />
              </div>
            ) : !loadingLaws ? (
              <RegulationsSidebar 
                laws={laws} 
                activeLawId={null}
                isSearchBarVisible={isSearchBarVisible}
              />
            ) : null}
          </div>
        )}
        
        {/* 메인 콘텐츠 (안전용품 페이지는 왼쪽 여백 최소화로 사이드바와 콘텐츠 간격 축소) */}
        <main
          className={`flex-1 transition-all duration-300 ${
            isHomePage
              ? 'w-full px-0 md:px-2 lg:px-4 xl:px-6 pt-0 pb-0 md:pb-2'
              : isSafetyMaterialsSection
                ? 'w-full p-2 sm:p-4 md:p-6'
                : isBoardPage
                  ? 'w-full p-2 sm:p-3 md:p-4'
                  : 'p-2 sm:p-4 md:p-6'
          }`}
        >
          <div className="fade-in">
            {children}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default MainLayout;
